import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import { Link, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faPen, faFolderOpen } from "@fortawesome/free-solid-svg-icons";
import HelmetInfo from "Components/HelmetInfo/HelmetInfo";
import ModalRequestAndAddProject from "Components/ModalRequestAndAddProject/ModalRequestAndAddProject";
import { toast } from "react-toastify";
import { collection, doc, getDocs, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase"; // Adjust import path if necessary
import HeaderPages from "Components/HeaderPages/HeaderPages";
import "./Projects.css";

const MyProjectsCards = () => {
  const { t } = useTranslation(); // Initialize the useTranslation hook
  const [cards, setCards] = useState([]);
  const [showModal, setShowModalSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const router = useNavigate();

  // Fetch cards from Firestore
  const fetchCards = async () => {
    setLoading(true);
    try {
      const collectionRef = collection(db, "OurService", "services", "Cards");
      const snapshot = await getDocs(collectionRef);
      const cardsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCards(cardsData);
    } catch (error) {
      console.error("Error fetching cards:", error);
      toast.error(t("failedToFetchServices") || "Failed to fetch services");
    } finally {
      setLoading(false);
    }
  };

  // Handle card deletion
  const handleDeleteCard = async (cardId) => {
    try {
      const docRef = doc(db, "OurService", "services", "Cards", cardId);
      await deleteDoc(docRef);
      // Remove the deleted card from state
      setCards(cards.filter(card => card.id !== cardId));
      toast.success(t("CardDeletedSuccessfully"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Error deleting card:", error);
      toast.error(t("CardDeletionFailed"), {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 2000,
      });
    }
  };

  // Fetch cards on component mount
  useEffect(() => {
    fetchCards();
  }, []);

  // Handle modal actions
  const handleCloseModal = () => {
    setShowModalSuccess(false);
    router("/home");
  };

  const handleOpenModalSuccess = () => {
    setShowModalSuccess(true);
  };

  return (
    <>
      <HelmetInfo titlePage={t("myProjects")} />
      <ModalRequestAndAddProject
        showModal={showModal}
        handleCloseModal={handleCloseModal}
      />
      <div className="project-management-request all-main my-projects-container">

        {/* Render Page Breadcrumb / Title Navigation */}
        <HeaderPages titlePage={t("myProjects")} />

        {/* Enhanced Header Section */}
        <div className="my-projects-header">
          <div className="header-info">
            <h1>{t("myProjects")}</h1>
            <p>{t("Manage and update clinic services or sections shown to customers")}</p>
          </div>
          <Link
            className="btn-add-service"
            to={"/AddService"}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t("Add New Service")}
          </Link>
        </div>

        <div className="form-project-management">
          <div className="row Servcis-Cards g-4">
            {loading ? (
              // Skeleton Loader State
              Array.from({ length: 4 }).map((_, index) => (
                <div className="col-lg-3 col-md-4 col-sm-6" key={`skeleton-${index}`}>
                  <div className="skeleton-card">
                    <div className="skeleton-icon"></div>
                    <div className="skeleton-title"></div>
                    <div className="skeleton-text"></div>
                    <div className="skeleton-text"></div>
                    <div className="skeleton-text short"></div>
                    <div className="skeleton-actions">
                      <div className="skeleton-btn-edit"></div>
                      <div className="skeleton-btn-delete"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : cards.length === 0 ? (
              // Enhanced Empty State
              <div className="col-12 text-center">
                <div className="my-projects-empty-state">
                  <div className="empty-state-icon-container">
                    <FontAwesomeIcon icon={faFolderOpen} />
                  </div>
                  <h3>{t("No Services Found")}</h3>
                  <p>
                    {t("You haven't added any services yet. Create your first service to showcase it on your landing page.")}
                  </p>
                  <Link
                    className="btn-add-service"
                    to={"/AddService"}
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    {t("Add First Service")}
                  </Link>
                </div>
              </div>
            ) : (
              // Cards Grid
              cards.map((card) => (
                <div className="col-lg-3 col-md-4 col-sm-6" key={card.id}>
                  <div className="service-card">
                    <div className="service-icon">
                      {card.iconUrl ? (
                        <img src={card.iconUrl} alt={card.title} />
                      ) : (
                        <FontAwesomeIcon icon={faFolderOpen} style={{ fontSize: "24px", color: "var(--blue-main-color)" }} />
                      )}
                    </div>
                    <h5>{card.title}</h5>
                    <p>{card.homeTxt}</p>
                    <div className="card-actions">
                      <Link
                        className="btn-card-action-edit"
                        to={`/AddService?edit=${card.id}`}
                      >
                        <FontAwesomeIcon icon={faPen} />
                        <span>{t("Edit")}</span>
                      </Link>
                      <button
                        className="btn-card-action-delete"
                        onClick={() => handleDeleteCard(card.id)}
                        title={t("Delete")}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MyProjectsCards;
